/**
 * Тексты карточки бренда (§7) — свой словарь модуля.
 *
 * Правило спеки §12: ни одной строки текста в компонентах. Общий `texts.ts`
 * читают сразу несколько волн разработки, поэтому раздел карточки живёт здесь
 * и берёт из общего файла только склонение числительных.
 *
 * Дословные строки из §12 помечены ссылкой на пункт. Строки без пометки
 * «§12» спека не диктует — они перечислены в отчёте модуля.
 */
import { countOf, forms, plural } from '../../../texts.js'

/**
 * Формы слова «поле» — в общем словаре их нет, а общий файл правит другая
 * волна, поэтому склонение модуля живёт рядом с его текстами.
 */
const fieldForms = ['поле', 'поля', 'полей'] as const

export const cardTexts = {
  // ───────────────────────────── шапка панели (§7) ─────────────────────────
  head: {
    /** Доступное имя панели: «Карточка бренда Barazza». */
    panelLabel: (brand: string) => `Карточка бренда ${brand}`,
    close: 'Закрыть карточку',
    copyLink: 'Скопировать ссылку на бренд',
    openPage: 'Открыть карточку отдельной страницей',
    actions: 'Действия с брендом',
    /** §12.6 */
    linkCopied: 'Ссылка скопирована',
    linkCopyFailed: 'Не удалось скопировать ссылку — скопируйте адрес из строки браузера',
    draftBrand: 'новый',
    archived: 'архив',
    /** §4.3: новый бренд виден вне фильтра (§12.3). */
    draftBrandHint: 'Новый бренд показан вне фильтра, пока вы его не подтвердили',
    inChannels: 'Ведётся в каналах',
    presentIn: (name: string) => `Ведётся в ${name}`,
    absentIn: (name: string) => `Не ведётся в ${name}`,
  },

  menu: {
    rename: 'Переименовать бренд',
    history: 'История бренда',
    comment: 'Написать комментарий',
    archive: 'Архивировать бренд',
    /** §12.7 */
    archiveDenied: 'Нет права архивировать бренды. Доступно коммерческому директору и собственнику',
  },

  rename: {
    label: 'Название бренда',
    /** §5.8, §12.3 — дословно */
    consequence: 'Название изменится во всех каналах, в истории и в уведомлениях',
    save: 'Переименовать',
    cancel: 'Отмена',
    /** §12.7 */
    required: 'Поле обязательно к заполнению',
  },

  archive: {
    /** §5.8 — дословно */
    title: (brand: string) => `Архивировать ${brand}?`,
    text: 'Бренд уйдёт из таблицы и останется в истории. Вернуть можно фильтром «Статус: архив»',
    confirm: 'Архивировать',
    cancel: 'Отмена',
  },

  // ───────────────────────────── каналы (§7) ───────────────────────────────
  channels: {
    tabsLabel: 'Канал',
    /** Точка на вкладке: значения отличаются от текущей. */
    differs: 'значения отличаются',
    /** §12.3 — дословно */
    absent: (brand: string, channel: string) => `${brand} не ведётся в ${channel}`,
    /** §12.1 — дословно */
    addTo: (channel: string) => `Добавить в ${channel}`,
    /** §12.1 — дословно */
    copyFrom: (channel: string) => `Скопировать значения из ${channel}`,
    copyTitle: (from: string, to: string) => `Скопировать значения из ${from} в ${to}`,
    copyHint: 'Отметьте поля, которые нужно скопировать. Это одна операция в черновике.',
    copyConfirm: (n: number) => `Скопировать ${countOf(n, fieldForms)}`,
    copyNothing: 'Нечего копировать: в исходном канале эти поля пусты',
    copyAll: 'Выбрать все',
    copyNone: 'Снять все',
  },

  // ───────────────────────────── блоки (§7) ───────────────────────────────
  blocks: {
    site: 'На сайте',
    priority: 'Приоритет',
    promo: 'Продвижение',
    service: 'Сервис White Glove и заметки',
    /** «Отвечает Лена Усова» / «Отвечают Кирилл Горчатов и Гоша Терновский» (§4.9, §7) */
    owner: (names: string[]) =>
      names.length === 0
        ? ''
        : `${names.length === 1 ? 'Отвечает' : 'Отвечают'} ${names.join(' и ')}`,
    /** Счётчик на свёрнутом блоке: «(0/4)». */
    filled: (filled: number, total: number) => `(${filled}/${total})`,
    filledLabel: (filled: number, total: number) =>
      `заполнено ${filled} из ${total} ${plural(total, fieldForms)}`,
    hasDraft: 'внутри есть правка черновика',
    collapse: 'Свернуть блок',
    expand: 'Развернуть блок',
  },

  // ───────────────────────────── поля (§7, sketch) ────────────────────────
  fields: {
    isPresent: 'Ведём в канале',
    visibility: 'Видимость на сайте',
    url: 'Адрес страницы',
    stockDisplay: 'Статус наличия',
    oos: 'Нет в наличии',
    tier: 'Тир бренда',
    categoriesUnclear: 'Категории не уточнены',
    seoPriority: 'Приоритет SEO',
    contextPriority: 'Приоритет Контекст',
    delivery: 'Доставка',
    installation: 'Установка',
    warranty: 'Гарантия',
    note: 'Примечание',
  },

  value: {
    empty: '— не задано',
    /** «было: B» и «было: пусто» (§5.5, §12.3) */
    was: (label: string) => `было: ${label}`,
    wasEmpty: 'было: пусто',
    /** П1: черновик видите только вы. */
    draftHint: 'правка в черновике — видите только вы',
    yes: 'Да',
    no: 'Нет',
    tierEmpty: '—',
    tierEmptyLabel: 'тир не задан',
    clear: 'Очистить',
    openLink: 'Открыть страницу бренда',
  },

  deny: {
    /** §12.7 — дословно (роль «Пополняющий») */
    filledForContributor:
      'Ваша роль позволяет только добавлять новое. Изменить заполненное значение нельзя — напишите комментарий, его увидят все',
    /** §11.4: наблюдатель ничего не правит; дословной строки спека не даёт */
    viewer: 'Ваша роль позволяет только смотреть данные',
  },

  consequence: {
    /** §5.8, §12.3 — дословно */
    visibilityNo: 'Бренд исчезнет со страниц сайта после публикации',
    /** спека строки для этого случая не даёт — см. отчёт модуля */
    presentOff: (channel: string) => `Бренд перестанет вестись в ${channel}: поля канала станут недоступны`,
    /** §12.7 */
    urlInvalid: 'Похоже, это не адрес страницы — сохраняется только ссылка вида https://…',
  },

  /** Пользовательские поля (§4.4) */
  custom: {
    /** §5.2: нечисловой ввод не принимается, под полем строка-ошибка */
    notANumber: 'Здесь можно ввести только число',
    /** §12.7 — дословно */
    outOfRange: (min: number, max: number) => `Значение должно быть от ${min} до ${max}`,
  },

  // ───────────────────────── зона ответственности (§4.9) ──────────────────
  missing: {
    /** «▲ Пропущено для вас: 2 →» */
    label: (n: number) => `Пропущено для вас: ${n}`,
    hint: 'Пустые поля из вашей зоны ответственности',
    go: 'Перейти к первому',
  },

  // ───────────────────────────── категории (§7) ───────────────────────────
  categories: {
    title: (n: number) => `Категории (${n})`,
    /** §12.1 — дословно */
    add: 'Добавить',
    addLabel: 'Добавить категорию из справочника',
    /** §12.8 — дословно */
    emptyTitle: 'категории не заданы',
    pickerLabel: 'Категории справочника',
    search: 'Поиск по справочнику',
    searchPlaceholder: 'Начните вводить название',
    /** §9.1: в справочнике 46 категорий со счётчиками использования. */
    usage: (n: number) => `${n} ${plural(n, forms.brand)}`,
    attached: 'уже привязана',
    nothingFound: 'В справочнике ничего не нашлось',
    /** §12.7 — дословно */
    freeInputDenied: 'Категорию можно только выбрать из справочника',
    /** §12.3 — дословно */
    requestLink: 'Нет такой категории? Запросить добавление',
    requestTitle: 'Запросить добавление категории',
    requestPlaceholder: 'Название категории',
    requestSubmit: 'Запросить',
    requestBody: (name: string) =>
      `Прошу добавить в справочник категорию «${name}» — её не хватает для этого бренда.`,
    requestSent: 'Запрос ушёл комментарием к бренду — его увидят владельцы справочника',
    /** После выбора сразу спрашиваем тир (§7). */
    tierPromptLabel: (channel: string) => `Приоритет в ${channel}:`,
    tierLater: 'позже',
    // Буквы «Р» и «О» рядом с селектом не читались: пишем канал целиком.
    channelShort: (name: string) => name,
    tierIn: (channel: string) => `Приоритет в ${channel}`,
    detach: 'Отвязать категорию',
    /** §5.8 — дословно текст последствия */
    detachConsequence: (category: string, brand: string) =>
      `Отвязать «${category}» от ${brand}? Приоритеты этой категории в Рознице и в Опте перестанут учитываться`,
    detachConfirm: 'Отвязать',
    detachCancel: 'Отмена',
    detachedMark: 'отвязана в черновике',
    draftMark: 'привязана в черновике',
    /** §12.7 */
    attachDenied: 'Нет права привязывать категории. Доступно директорам и собственнику',
    detachDenied: 'Нет права отвязывать категории. Доступно директорам и собственнику',
  },

  // ───────────────────────────── вкладки низа (§7) ────────────────────────
  tabs: {
    label: 'Обсуждение',
    asideTitle: 'Обсуждение бренда',
    brandTalk: (brand: string) => `Обсуждение бренда ${brand}`,
    comments: 'Комментарии',
    history: 'История',
  },

  comments: {
    /* Задачи в ленте: срок, галочка, отчёт и перевод заметки в задачу. */
    due: (date: string) => `до ${date}`,
    markDone: 'Отметить выполненной',
    closeTask: 'Задача выполнена',
    reportPlaceholder: 'Что сделано? Это увидят все',
    doneBy: (who: string) => `Выполнено · ${who}`,
    donePending: 'Выполнено · ждёт подтверждения',
    toTask: 'Сделать задачей',
    toComment: 'Сделать комментарием',
    more: 'Ещё',
    reply: 'Ответить',
    replyCancel: 'Не отвечать',
    /** §12.8 — дословно */
    emptyTitle: 'Комментариев пока нет',
    emptyText: 'Напишите первый — его увидят все после подтверждения',
    placeholder: 'Написать комментарий…',
    submit: 'Добавить комментарий',
    formatHint: '**жирный** · *курсив* · `код` · «- » список · Cmd+B, Cmd+I, Cmd+E',
    submitHint: 'Enter — добавить, Shift+Enter — перенос строки',
    /** П1 */
    draftMark: 'в черновике',
    editedMark: 'изменён',
    edit: 'Изменить',
    editSave: 'Сохранить',
    editCancel: 'Отмена',
    /** 15 минут — окно правки своего комментария (§7). */
    editWindow: 'Свой комментарий можно изменить в течение 15 минут',
    remove: 'Удалить комментарий',
    /** §5.8 — дословно */
    removeTitle: 'Удалить комментарий?',
    removeText: 'Он останется в истории изменений',
    removeConfirm: 'Удалить',
    removeCancel: 'Отмена',
    /** §12.7 */
    denied: 'Нет права писать комментарии',
    loadFailed: 'Не удалось загрузить комментарии',
  },

  history: {
    /** §12.8 — дословно */
    emptyTitle: 'Правок по этому бренду ещё не было',
    emptyText: 'Данные пришли из Google-таблицы при импорте',
    loadFailed: 'Не удалось загрузить историю бренда',
    /** §12.7 — дословно */
    denied: 'Нет права смотреть историю. Доступно директорам и собственнику',
    /** Пакет в таймлайне: «#128 · Лена Усова · 14.10 в 16:42» */
    pack: (id: number) => `#${id}`,
    packLabel: (id: number) => `Открыть пакет #${id}`,
    changes: (n: number) => countOf(n, forms.change),
    onlyField: (label: string) => `только поле: ${label}`,
    onlyFieldClear: 'Показать все правки',
    filterByField: (label: string) => `Показать в истории только правки поля «${label}»`,
    systemAuthor: 'Импорт из Google-таблицы',
    unknownAuthor: 'Неизвестный автор',
    arrow: '→',
    empty: 'пусто',
    action: {
      create: 'Бренд создан',
      archive: 'Бренд архивирован',
      restore: 'Бренд восстановлен',
      attach: (category: string) => `Категория привязана: ${category}`,
      detach: (category: string) => `Категория отвязана: ${category}`,
      comment: 'Комментарий',
      deleteComment: 'Комментарий удалён',
    },
    more: (n: number) => `Показано 100 правок из ${n}. Остальное — в истории изменений`,
    openFull: 'Открыть историю изменений',
  },

  // ───────────────────────────── состояния (§11) ──────────────────────────
  state: {
    notFoundTitle: 'Бренд не найден',
    notFoundText: (slug: string) => `Бренда с адресом «${slug}» нет в срезе. Возможно, он архивирован.`,
    /** §12.1 — дословно */
    back: 'Назад к таблице',
    loadingLabel: 'Загружаем карточку бренда',
  },

  nav: {
    label: 'Переход по брендам',
    prev: 'Предыдущий бренд',
    next: 'Следующий бренд',
  },

  // ───────────────────────── создание бренда (§7) ─────────────────────────
  create: {
    title: 'Новый бренд',
    name: 'Название',
    namePlaceholder: 'Например, Asko',
    /** §12.7 */
    nameRequired: 'Поле обязательно к заполнению',
    /** §12.7 — дословно, если имя занято уже в срезе */
    nameTakenLocal: (brand: string) => `Бренд с таким названием уже есть: ${brand}.`,
    nameTakenOpen: 'Открыть?',
    channels: 'Каналы',
    channelsHint: 'Бренд можно вести в одном канале или в двух. Тир, приоритеты и ссылка у каждого канала свои.',
    channelsRequired: 'Выберите хотя бы один канал',
    tier: 'Тир бренда',
    tierIn: (channel: string) => `Тир в канале «${channel}»`,
    tierHint: 'В Рознице и Опте тир может отличаться — поэтому спрашиваем по каждому каналу отдельно',
    tierRequired: 'Поставьте тир в каждом выбранном канале',
    categories: 'Категории',
    categoriesHint: 'Только из справочника. Каждая категория получит тир бренда — поменять его можно в карточке, тоже по каждому каналу отдельно.',
    submit: 'Создать бренд',
    cancel: 'Отмена',
    /** П1: созданный бренд живёт в черновике до подтверждения. */
    footerNote: 'Бренд появится у остальных после подтверждения черновика',
    /** §12.7 */
    denied: 'Нет права создавать бренды',
    created: (brand: string) => `${brand} добавлен в черновик`,
    failed: 'Не удалось создать бренд',
  },
} as const
